import { useEffect, useState } from 'react';
import { Check, Moon, Sun, Trash2, AlertTriangle, Lock, Eye, EyeOff, ChevronDown, KeyRound, Palette } from 'lucide-react';
import apiClient from '../api/apiClient';
import { useTheme } from '../context/ThemeContext';
import Select from './ui/Select';

/* ── Mini preview that mimics the app's sidebar + card layout ── */
const ThemePreview = ({ colors, fontClass }) => {
  const navDots = [true, false, false, false, false];

  return (
    <div
      className={`relative h-32 w-full overflow-hidden rounded-lg border ${fontClass || ''}`}
      style={{ backgroundColor: colors.bg, borderColor: colors.border }}
    >
      {/* Left sidebar */}
      <div
        className="absolute left-0 top-0 h-full w-9 flex flex-col items-center gap-2.5 pt-3"
        style={{ backgroundColor: colors.surface, borderRight: `1px solid ${colors.border}` }}
      >
        {navDots.map((active, i) => (
          <div
            key={i}
            className="h-2.5 w-2.5 rounded-sm"
            style={{ backgroundColor: active ? colors.accent : colors.text + '30' }}
          />
        ))}
      </div>

      {/* Main content area */}
      <div className="absolute inset-0 left-9 p-3 flex gap-3">
        {/* Balance card */}
        <div
          className="flex-1 rounded-md p-2.5 flex flex-col gap-1.5"
          style={{
            backgroundColor: colors.surface,
            boxShadow: `0 2px 8px ${colors.accent}20`,
            border: `1px solid ${colors.border}`,
          }}
        >
          <div className="h-1.5 w-10 rounded-full" style={{ backgroundColor: colors.accent }} />
          <div className="h-4 w-14 rounded-sm" style={{ backgroundColor: colors.accent + '80' }} />
          <div className="h-1.5 w-8 rounded-full" style={{ backgroundColor: colors.text + '30' }} />
          <div className="h-1.5 w-12 rounded-full" style={{ backgroundColor: colors.text + '20' }} />
        </div>
        {/* Form card */}
        <div
          className="flex-1 rounded-md p-2.5 flex flex-col gap-1.5"
          style={{
            backgroundColor: colors.surface,
            border: `1px solid ${colors.border}`,
          }}
        >
          <div className="h-1.5 w-8 rounded-full" style={{ backgroundColor: colors.text + '30' }} />
          <div className="h-2.5 w-full rounded-sm" style={{ backgroundColor: colors.text + '10', border: `1px solid ${colors.border}` }} />
          <div className="h-2.5 w-full rounded-sm" style={{ backgroundColor: colors.text + '10', border: `1px solid ${colors.border}` }} />
          <div
            className="mt-auto h-4 w-14 rounded-sm self-end"
            style={{ backgroundColor: colors.accent }}
          />
        </div>
      </div>
    </div>
  );
};

const ThemeCard = ({ themeId, config, isActive, isDark, onSelect }) => {
  const colors = isActive && config.hasDarkMode && isDark
    ? config.preview.dark
    : config.preview.light;

  return (
    <button
      onClick={() => onSelect(themeId)}
      className={`group relative w-full rounded-xl3 border p-5 text-left transition-all duration-200 ${
        isActive
          ? 'border-accent bg-surface shadow-glass'
          : 'border-line bg-glass hover:border-accent/50 hover:shadow-glass'
      }`}
      type="button"
    >
      {/* Active checkmark */}
      {isActive && (
        <span className="absolute right-4 top-4 flex h-6 w-6 items-center justify-center rounded-full bg-accent text-accent-ink">
          <Check size={13} />
        </span>
      )}

      {/* Preview */}
      <ThemePreview colors={colors} />

      {/* Info */}
      <div className="mt-4">
        <p className="text-sm font-semibold text-ink">{config.name}</p>
        <p className="mt-0.5 text-xs text-muted">{config.description}</p>
      </div>

      {/* Color palette dots */}
      <div className="mt-3 flex items-center gap-1.5">
        <span className="h-3 w-3 rounded-full border border-line" style={{ backgroundColor: colors.bg }} />
        <span className="h-3 w-3 rounded-full border border-line" style={{ backgroundColor: colors.surface }} />
        <span className="h-3 w-3 rounded-full border border-line" style={{ backgroundColor: colors.accent }} />
        <span className="h-3 w-3 rounded-full border border-line" style={{ backgroundColor: colors.text }} />
      </div>
    </button>
  );
};

const PasswordField = ({ label, value, onChange, show, onToggle, placeholder }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-1)' }}>{label}</label>
    <div style={{ position: 'relative' }}>
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: '100%',
          padding: '9px 40px 9px 12px',
          borderRadius: 10,
          border: '1px solid var(--line-0)',
          background: 'var(--base-0)',
          color: 'var(--ink-0)',
          fontSize: 14,
          outline: 'none',
          boxSizing: 'border-box',
        }}
      />
      <button
        type="button"
        onClick={onToggle}
        style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-2)', display: 'flex', alignItems: 'center' }}
      >
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  </div>
);

export const AccordionSection = ({ icon: Icon, title, expanded, onToggle, children, danger = false }) => (
  <section className={`overflow-hidden rounded-2xl border bg-surface shadow-glass ${danger ? 'border-accent/40' : 'border-line'}`}>
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      className="flex w-full items-center gap-3 px-5 py-4 text-left transition hover:bg-base/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent"
    >
      <span className={`flex h-8 w-8 items-center justify-center rounded-xl border ${danger ? 'border-accent/30 text-accent' : 'border-line text-muted'}`}><Icon size={16} /></span>
      <span className="flex-1 text-xs font-semibold uppercase tracking-[0.16em] text-muted">{title}</span>
      <ChevronDown size={16} className={`text-muted transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
    </button>
    {expanded && <div className="border-t border-line">{children}</div>}
  </section>
);

const SettingsPage = () => {
  const { activeTheme, isDark, changeTheme, toggleTheme, themes, themeConfig } = useTheme();
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', next: '', confirm: '' });
  const [showPass, setShowPass] = useState({ current: false, next: false, confirm: false });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [apiKeys, setApiKeys] = useState([]);
  const [apiKeyName, setApiKeyName] = useState('MCP');
  const [apiKeyDays, setApiKeyDays] = useState(90);
  const [newApiKey, setNewApiKey] = useState('');
  const [expandedSections, setExpandedSections] = useState({ appearance: false, security: false, apiKeys: false, danger: false });
  const loadApiKeys = () => apiClient.get('/api-keys').then(r => setApiKeys(r.data.data || [])).catch(() => {});
  useEffect(() => { loadApiKeys(); }, []);
  const createApiKey = async () => { try { const r = await apiClient.post('/api-keys', { name: apiKeyName, days: apiKeyDays }); setNewApiKey(r.data.data.key); loadApiKeys(); } catch (_) {} };
  const revokeApiKey = async (id) => { await apiClient.post(`/api-keys/${id}/revoke`); loadApiKeys(); };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordForm({ current: '', next: '', confirm: '' });
    setShowPass({ current: false, next: false, confirm: false });
    setPasswordError('');
    setPasswordSuccess(false);
  };

  const handlePasswordChange = async () => {
    setPasswordError('');
    if (!passwordForm.current) { setPasswordError('Введите текущий пароль'); return; }
    if (passwordForm.next.length < 8) { setPasswordError('Новый пароль должен содержать не менее 8 символов'); return; }
    if (!/[A-Z]/.test(passwordForm.next)) { setPasswordError('Новый пароль должен содержать заглавную букву'); return; }
    if (!/[a-z]/.test(passwordForm.next)) { setPasswordError('Новый пароль должен содержать строчную букву'); return; }
    if (!/[0-9]/.test(passwordForm.next)) { setPasswordError('Новый пароль должен содержать цифру'); return; }
    if (passwordForm.next !== passwordForm.confirm) { setPasswordError('Пароли не совпадают'); return; }

    setPasswordLoading(true);
    try {
      await apiClient.post('/auth/password/change', {
        currentPassword: passwordForm.current,
        newPassword: passwordForm.next,
      });
      setPasswordSuccess(true);
    } catch (err) {
      setPasswordError(err?.response?.data?.message || 'Ошибка при смене пароля');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleReset = async () => {
    setResetLoading(true);
    setResetError('');
    try {
      await apiClient.delete('/transactions/all');
      setShowResetConfirm(false);
    } catch (err) {
      console.error('Error resetting transactions:', err);
      setResetError(err?.response?.data?.message || 'Не удалось удалить данные');
    } finally {
      setResetLoading(false);
    }
  };
  const toggleSection = (section) => setExpandedSections((current) => ({ ...current, [section]: !current[section] }));

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6">
      {/* Password change modal */}
      {showPasswordModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={closePasswordModal}
        >
          <div
            style={{ background: 'var(--surface-0)', borderRadius: 16, padding: 32, maxWidth: 420, width: '100%', border: '1px solid var(--line-0)' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
              <span style={{ width: 52, height: 52, borderRadius: '50%', background: 'var(--accent-0)20', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Lock size={24} style={{ color: 'var(--accent-0)' }} />
              </span>
            </div>

            {passwordSuccess ? (
              <>
                <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--ink-0)', textAlign: 'center', marginBottom: 8 }}>Пароль изменён</h3>
                <p style={{ fontSize: 14, color: 'var(--ink-2)', textAlign: 'center', marginBottom: 28 }}>Новый пароль успешно сохранён.</p>
                <button onClick={closePasswordModal} className="btn-secondary" style={{ width: '100%' }}>Закрыть</button>
              </>
            ) : (
              <>
                <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--ink-0)', textAlign: 'center', marginBottom: 24 }}>Смена пароля</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 24 }}>
                  <PasswordField
                    label="Текущий пароль"
                    value={passwordForm.current}
                    onChange={v => setPasswordForm(f => ({ ...f, current: v }))}
                    show={showPass.current}
                    onToggle={() => setShowPass(s => ({ ...s, current: !s.current }))}
                    placeholder="Введите текущий пароль"
                  />
                  <PasswordField
                    label="Новый пароль"
                    value={passwordForm.next}
                    onChange={v => setPasswordForm(f => ({ ...f, next: v }))}
                    show={showPass.next}
                    onToggle={() => setShowPass(s => ({ ...s, next: !s.next }))}
                    placeholder="Минимум 8 символов"
                  />
                  <PasswordField
                    label="Подтвердите новый пароль"
                    value={passwordForm.confirm}
                    onChange={v => setPasswordForm(f => ({ ...f, confirm: v }))}
                    show={showPass.confirm}
                    onToggle={() => setShowPass(s => ({ ...s, confirm: !s.confirm }))}
                    placeholder="Повторите новый пароль"
                  />
                  {passwordError && (
                    <p style={{ fontSize: 13, color: '#dc2626', margin: 0 }}>{passwordError}</p>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button onClick={closePasswordModal} className="btn-secondary" style={{ flex: 1 }} disabled={passwordLoading}>Отмена</button>
                  <button
                    onClick={handlePasswordChange}
                    disabled={passwordLoading}
                    className="btn-primary"
                    style={{ flex: 1 }}
                  >
                    {passwordLoading ? 'Сохранение...' : 'Сохранить'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Confirm modal */}
      {showResetConfirm && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setShowResetConfirm(false)}
        >
          <div
            style={{ background: 'var(--surface-0)', borderRadius: 16, padding: 32, maxWidth: 420, width: '100%', border: '1px solid var(--line-0)' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
              <span style={{ width: 52, height: 52, borderRadius: '50%', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AlertTriangle size={24} style={{ color: '#dc2626' }} />
              </span>
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--ink-0)', textAlign: 'center', marginBottom: 8 }}>
              Начать сначала?
            </h3>
            <p style={{ fontSize: 14, color: 'var(--ink-2)', textAlign: 'center', marginBottom: 28, lineHeight: 1.5 }}>
              Все транзакции и кредиты будут удалены безвозвратно. Справочники (категории, источники дохода, места, теги) останутся нетронутыми.
            </p>
            {resetError && <p role="alert" style={{ color: '#dc2626', textAlign: 'center', marginBottom: 16 }}>{resetError}</p>}
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => setShowResetConfirm(false)}
                className="btn-secondary"
                style={{ flex: 1 }}
                disabled={resetLoading}
              >
                Отмена
              </button>
              <button
                onClick={handleReset}
                disabled={resetLoading}
                style={{ flex: 1, padding: '10px 0', borderRadius: 10, background: '#dc2626', color: '#fff', fontWeight: 600, border: 'none', cursor: resetLoading ? 'not-allowed' : 'pointer', opacity: resetLoading ? 0.6 : 1 }}
              >
                {resetLoading ? 'Удаление...' : 'Удалить всё'}
              </button>
            </div>
          </div>
        </div>
      )}

      <header className="border-b border-line pb-5">
        <h2 className="text-3xl font-semibold text-ink">Настройки</h2>
        <p className="mt-1 text-sm text-muted">Управление оформлением, безопасностью и доступом к данным.</p>
      </header>

      <div className="space-y-2.5">
        <AccordionSection icon={Palette} title="Оформление" expanded={expandedSections.appearance} onToggle={() => toggleSection('appearance')}>
          <div className="space-y-6 p-5">
            <div><p className="text-sm font-semibold text-ink">Тема оформления</p><p className="mt-1 text-xs leading-5 text-muted">Каждая тема полностью меняет стиль, палитру и типографику интерфейса.</p></div>
            <div className="grid gap-4 sm:grid-cols-2">{Object.entries(themes).map(([id, config]) => <ThemeCard key={id} themeId={id} config={config} isActive={activeTheme === id} isDark={isDark} onSelect={changeTheme} />)}</div>
            {themeConfig.hasDarkMode && (
              <div className="border-t border-line pt-5">
                <p className="mb-3 text-sm font-semibold text-ink">Цветовая схема</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <button onClick={() => isDark && toggleTheme()} type="button" className={`flex items-center gap-4 rounded-2xl border p-4 text-left transition ${!isDark ? 'border-accent bg-surface shadow-glass' : 'border-line bg-glass hover:border-accent/50'}`}>
                    <span className={`flex h-10 w-10 items-center justify-center rounded-xl border ${!isDark ? 'border-accent text-accent' : 'border-line text-ink-subtle'}`}><Sun size={20} /></span>
                    <span><span className="block text-sm font-semibold text-ink">Светлая</span><span className="text-xs text-muted">Тёплый фон, светлые карточки</span></span>
                    {!isDark && <Check size={16} className="ml-auto text-accent" />}
                  </button>
                  <button onClick={() => !isDark && toggleTheme()} type="button" className={`flex items-center gap-4 rounded-2xl border p-4 text-left transition ${isDark ? 'border-accent bg-surface shadow-glass' : 'border-line bg-glass hover:border-accent/50'}`}>
                    <span className={`flex h-10 w-10 items-center justify-center rounded-xl border ${isDark ? 'border-accent text-accent' : 'border-line text-ink-subtle'}`}><Moon size={20} /></span>
                    <span><span className="block text-sm font-semibold text-ink">Тёмная</span><span className="text-xs text-muted">Тёмный фон, мягкий контраст</span></span>
                    {isDark && <Check size={16} className="ml-auto text-accent" />}
                  </button>
                </div>
              </div>
            )}
          </div>
        </AccordionSection>

        <AccordionSection icon={Lock} title="Безопасность" expanded={expandedSections.security} onToggle={() => toggleSection('security')}>
          <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center"><div className="min-w-0 flex-1"><p className="text-sm font-semibold text-ink">Пароль</p><p className="mt-1 text-xs leading-5 text-muted">Управление паролем учётной записи.</p></div><button onClick={() => setShowPasswordModal(true)} type="button" className="inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-line bg-surface px-5 text-sm font-semibold text-ink transition hover:border-accent/50"><Lock size={16} className="text-accent" />Сменить пароль</button></div>
        </AccordionSection>

        <AccordionSection icon={KeyRound} title="API-ключи" expanded={expandedSections.apiKeys} onToggle={() => toggleSection('apiKeys')}>
          <div className="p-5"><p className="text-sm text-muted">Ключ даёт MCP доступ только к вашим данным.</p>
            <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="flex-1"><span aria-hidden="true" className="mb-2 block h-4 text-xs uppercase tracking-[0.24em]">&nbsp;</span><input aria-label="Название ключа" value={apiKeyName} onChange={e => setApiKeyName(e.target.value)} className="h-12 w-full rounded-2xl border border-line bg-surface px-4 text-base text-ink outline-none focus:ring-2 focus:ring-accent" placeholder="Название ключа" /></div>
              <div className="w-full sm:w-36"><Select label="Срок" name="apiKeyDays" value={apiKeyDays ?? ''} onChange={e => setApiKeyDays(e.target.value ? Number(e.target.value) : null)}><option value="30">30 дней</option><option value="90">90 дней</option><option value="365">365 дней</option><option value="">Бессрочно</option></Select></div>
              <button onClick={createApiKey} className="btn-primary h-12 rounded-xl px-5" type="button">Создать ключ</button>
            </div>
            {newApiKey && <div className="mt-4 rounded-xl border border-accent bg-surface p-3 text-sm text-ink">Скопируйте ключ сейчас — повторно он не будет показан:<br /><code className="break-all text-accent">{newApiKey}</code></div>}
            <div className="mt-4 space-y-2">{apiKeys.map(key => <div key={key.id} className="flex flex-col gap-2 rounded-xl border border-line p-4 text-sm sm:flex-row sm:items-center sm:justify-between"><span>{key.name}<span className="ml-2 text-muted">{key.revoked_at ? 'отозван' : key.expires_at ? `до ${new Date(key.expires_at).toLocaleDateString('ru-RU')}` : 'бессрочный'}</span></span>{!key.revoked_at && <button onClick={() => revokeApiKey(key.id)} className="self-start text-sm font-medium text-accent hover:underline sm:self-auto">Отозвать</button>}</div>)}</div>
          </div>
        </AccordionSection>

        <AccordionSection icon={AlertTriangle} title="Опасная зона" danger expanded={expandedSections.danger} onToggle={() => toggleSection('danger')}>
          <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center"><div className="min-w-0 flex-1"><p className="text-sm font-semibold text-ink">Сброс данных</p><p className="mt-1 text-xs leading-5 text-muted">Удалить все транзакции и начать ведение учёта с нуля.</p></div><button onClick={() => setShowResetConfirm(true)} type="button" className="inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 px-5 text-sm font-semibold text-red-600 transition hover:bg-red-100"><Trash2 size={16} />Начать сначала</button></div>
        </AccordionSection>
      </div>

    </div>
  );
};

export default SettingsPage;
